/**
 * Application Initialization
 * Bootstraps the application and initializes all modules
 */

import { managers, audioState } from './state.js';
import { cacheElements, elements } from './dom-cache.js';

// Core module imports
import { VisualizerManager, VisualizerRegistry } from '../visualizer.js';
import { visualizerLoader } from '../lib/visualizer-loader.js';
import { LyricsRenderer } from '../lyrics-renderer.js';
import { perfMonitor } from './performance-monitor.js';

// Visualizer imports (registers with VisualizerRegistry on import)
import '../visualizations/bars.js';
import '../visualizations/bubbles.js';
import '../visualizations/bands-meter.js';
import '../visualizations/spectral-analyzer.js';
import '../visualizations/beat-pulse.js';
import '../visualizations/semitone-piano.js';
import '../visualizations/dancing-figure.js';
import '../visualizations/fireworks.js';
import '../visualizations/color-explosion.js';
import '../visualizations/laser-fan-rig.js';
import '../visualizations/neon-lightning-field.js';
import '../visualizations/aurora-ribbons.js';
import '../visualizations/shockwave-bloom.js';
import '../visualizations/glitch-prism-grid.js';
import '../visualizations/starfield-drift.js';
import '../visualizations/breathing-nebula.js';
import '../visualizations/koi-pond-ripples.js';
import '../visualizations/sleep-aurora-curtains.js';
import '../visualizations/lux-pond.js';
import '../visualizations/mandala-bloom.js';
import '../visualizations/mosaic-slideshow.js';
import '../visualizations/deep-star-cathedral.js';
import '../visualizations/iridescent-tile-pusher.js';
import '../visualizations/cosmic-aurora.js';

// Auth and entitlement imports
import { authService } from '../auth/auth-service.js';
import { authUI } from '../auth/auth-ui.js';
import { entitlementService } from '../entitlements/entitlement-service.js';
import { featureGate } from '../entitlements/feature-gate.js';
import { pricingModal } from '../payments/pricing-modal.js';
import { checkoutService } from '../payments/checkout-service.js';
import { transactionHistory } from '../payments/transaction-history.js';
import { accountSettings } from '../auth/account-settings.js';

// Feature module imports
import { setupFileHandlers } from './file-handler.js';
import { setupPlaylistButtonListeners, setupPlaylistSync, setupPlaylistHorizontalScroll, setupMobileCollapsiblePanels } from './playlist.js';
import { setupPlaybackControls } from './playback.js';
import { populateVizSelector, buildControlsUI, setupVizTypeChangeHandler, buildVisualizerGrid } from './visualizer-controls.js';
import { setupLyricsPanelListeners, setupLyricsSettingsListeners, setupPerformanceMonitorListeners } from './lyrics-panel.js';
import { setupPresetListeners, populatePresetSelect } from './presets.js';
import { presetService } from './preset-service.js';
import { setupDisplayModeListeners } from './display-modes.js';
import { setupLiveModeListeners } from './live-mode.js';
import { setupExportButton, setupResolutionChangeHandler, setupFpsChangeHandler, setupCodecBitrateHandlers, updateExportOptionsWithTiers, setupExportPlaylistSummary, setupExportStatusBar } from './export-controller.js';

// New UI module imports
import { initOverlayPanels, initDropModal, initAccountPanel, updateAccountPanel } from './overlay-panels.js';
import { initSettingsOverlay } from './settings-overlay.js';
import { initBackgroundController } from './background-controller.js';
import { initPlaylistOverlay } from './playlist-overlay.js';
import { initPlaylistGenerator } from './playlist-generator.js';
import { initLibraryBrowser } from './library-browser.js';
import { initSunoManager } from './suno-manager.js';
import { initSunoOnboarding } from './suno-onboarding.js';
import { isDevelopmentHost } from '../config/config.js';

// Analytics imports
import { initAnalytics, completeAnalyticsSetup, handleAuthStateChange } from './analytics/index.js';
import { trackPaywallShown } from './analytics/event-tracker.js';

// Extension integration (broadcasts auth state to ShowLark extension)
import '../lib/suno/extension-auth-bridge.js';

/**
 * Initialize dev mode tier override (DEV only)
 * Uses tierConfigService to fetch tier definitions from the database
 */
async function initDevMode() {
  if (!import.meta.env.DEV) return;

  // SECURITY: Defense in depth - also check hostname
  if (!isDevelopmentHost(window.location.hostname)) {
    console.warn('[Dev] Hostname not whitelisted for dev mode');
    return;
  }

  const { tierConfigService } = await import('../config/tier-config-service.js');

  // Cache for tier config (fetched once)
  let tierConfig = null;

  /**
   * Get tier config, fetching from server if needed
   */
  async function getTierConfig() {
    if (!tierConfig) {
      try {
        tierConfig = await tierConfigService.getTierConfig();
      } catch (error) {
        console.warn('[DEV] Could not fetch tier config from server:', error.message);
        return null;
      }
    }
    return tierConfig;
  }

  /**
   * Build fake entitlements from tier config
   */
  function buildFakeEntitlements(tier) {
    // Convert tier.features array to object format expected by entitlements
    const featuresObj = {};
    for (const feature of tier.features || []) {
      featuresObj[feature.key] = feature.value;
    }

    return {
      tier: tier.id,
      tierInfo: {
        displayName: tier.displayName,
        badgeLabel: tier.badgeLabel,
        isPaid: tier.isPaid
      },
      status: 'active',
      features: {
        allowedVisualizers: featuresObj.allowedVisualizers || ['bars', 'bubbles'],
        maxPlaylistSongs: featuresObj.maxPlaylistSongs || 3,
        monthlyExportMinutes: featuresObj.monthlyExportMinutes || 10,
        maxResolution: featuresObj.maxResolution || '1280x720',
        exportFps: featuresObj.exportFps || [30],
        canCreateCustomVisualizers: featuresObj.canCreateCustomVisualizers || false,
        watermarkFree: featuresObj.watermarkFree || false,
        prioritySupport: featuresObj.prioritySupport || false,
        maxPresets: featuresObj.maxPresets ?? 10,
        maxExportDurationSeconds: featuresObj.maxExportDurationSeconds ?? -1
      },
      usage: { monthlyMinutesRemaining: 999 },
      credits: { balance: 9999 },
      freeMode: true  // Dev mode = all features unlocked including custom visualizers
    };
  }

  const devTier = localStorage.getItem('devTierOverride');
  const originalGetEntitlements = entitlementService.getEntitlements.bind(entitlementService);

  if (devTier) {
    console.log(`[DEV] Tier override active: ${devTier}`);
  }

  // Override getEntitlements to check for dev tier
  entitlementService.getEntitlements = async function(forceRefresh) {
    const tierOverride = localStorage.getItem('devTierOverride');
    if (tierOverride) {
      const config = await getTierConfig();
      const tier = config?.tiers.find(t => t.id === tierOverride);
      if (tier) {
        const fakeEntitlements = buildFakeEntitlements(tier);
        // Also update cache so validateExportLocally works
        this.cache = fakeEntitlements;
        return fakeEntitlements;
      }
    }
    return originalGetEntitlements(forceRefresh);
  };

  // Override validateExport to skip server call in dev mode
  const originalValidateExport = entitlementService.validateExport.bind(entitlementService);
  entitlementService.validateExport = async function(params) {
    const tierOverride = localStorage.getItem('devTierOverride');
    if (tierOverride) {
      const config = await getTierConfig();
      const tier = config?.tiers.find(t => t.id === tierOverride);
      if (tier) {
        // Use local validation with fake entitlements
        return this.validateExportLocally(params);
      }
    }
    return originalValidateExport(params);
  };

  // Console helpers for toggling tier
  window.devTier = {
    set: async (tierId) => {
      const config = await getTierConfig();
      if (!config) {
        console.error('[DEV] Could not fetch tier config. Is the server running?');
        return;
      }
      const tier = config.tiers.find(t => t.id === tierId);
      if (!tier) {
        const validTiers = config.tiers.map(t => t.id).join(', ');
        console.error(`[DEV] Invalid tier. Valid tiers: ${validTiers}`);
        return;
      }
      localStorage.setItem('devTierOverride', tierId);
      console.log(`[DEV] Tier set to ${tierId}. Reloading...`);
      location.reload();
    },
    clear: () => {
      localStorage.removeItem('devTierOverride');
      console.log('[DEV] Tier override cleared. Reloading...');
      location.reload();
    },
    current: () => localStorage.getItem('devTierOverride') || '(real tier)',
    list: async () => {
      const config = await getTierConfig();
      if (!config) {
        console.error('[DEV] Could not fetch tier config.');
        return;
      }
      console.log('[DEV] Available tiers:');
      config.tiers.forEach(t => console.log(`  - ${t.id} (${t.displayName})`));
    }
  };

  console.log('[DEV] Helpers: devTier.set("symphony"), devTier.clear(), devTier.current(), devTier.list()');
}

/**
 * Initialize authentication and entitlements
 */
async function initAuth() {
  // Phase 1: Auth service init — fatal if it fails, nothing else can work
  try {
    await authService.init();
  } catch (error) {
    console.error('[App] Auth service failed to initialize:', error);
    return;
  }

  // Phase 2: Synchronous UI setup (cannot throw)
  VisualizerRegistry.setSupabaseClient(authService.getClient());
  authUI.init();
  initAccountPanel(authUI);
  pricingModal.init();
  transactionHistory.init();
  accountSettings.init();
  accountSettings.onUpgradeClick = () => pricingModal.show('account_settings');
  featureGate.setPaywallCallback(showPaywallModal);
  authUI.onUpgradeClick = () => pricingModal.show('user_menu');
  authUI.onAccountClick = () => accountSettings.show();
  authUI.onHistoryClick = () => transactionHistory.show();

  // Phase 3: Checkout service — independent, does not block entitlements or listeners
  try {
    await checkoutService.init();
  } catch (error) {
    console.error('[App] Checkout service init failed:', error);
  }

  // Phase 4: Initial entitlements fetch — independent, does not block listeners
  try {
    const entitlements = await entitlementService.getEntitlements();
    authUI.updateEntitlements(entitlements);
    const isReallyAuthenticated = authService.isAuthenticated() && !authService.isAnonymous();
    updateAccountPanel(isReallyAuthenticated, authService.getUser(), entitlements?.tier, entitlements?.freeMode);
    accountSettings.setFreeMode(entitlements?.freeMode === true);
  } catch (error) {
    console.error('[App] Initial entitlements fetch failed:', error);
  }

  // Phase 5: Event listeners — MUST always register regardless of earlier failures
  entitlementService.onEntitlementsChange((newEntitlements) => {
    authUI.updateEntitlements(newEntitlements);
    const isRealUser = authService.isAuthenticated() && !authService.isAnonymous();
    updateAccountPanel(isRealUser, authService.getUser(), newEntitlements?.tier, newEntitlements?.freeMode);
    accountSettings.setFreeMode(newEntitlements?.freeMode === true);
  });

  authService.onAuthStateChange(async (event, session) => {
    await handleAuthStateChange(event, session);
    entitlementService.clearCache();
    visualizerLoader.clearUserVisualizersCache();
    VisualizerRegistry.clearCache();

    const newEntitlements = await entitlementService.getEntitlements(true);
    authUI.updateEntitlements(newEntitlements);
    const isRealUser = !!session && !session.user?.is_anonymous;
    updateAccountPanel(isRealUser, session?.user, newEntitlements?.tier, newEntitlements?.freeMode);
    accountSettings.setFreeMode(newEntitlements?.freeMode === true);

    await populateVizSelector();
    await buildVisualizerGrid();
    await updateExportOptionsWithTiers();
  });

  // Phase 6: Preset service — independent, does not affect other initialization
  try {
    await presetService.init();
  } catch (error) {
    console.error('[App] Preset service init failed:', error);
  }

  console.log('[App] Auth initialized');
}

/**
 * Show paywall modal
 */
function showPaywallModal(options) {
  // Track paywall shown event
  trackPaywallShown(options.feature || 'unknown', options.triggerLocation || 'unknown');

  // Create paywall modal if it doesn't exist
  let modal = document.getElementById('paywallModal');
  if (!modal) {
    const modalHTML = `
      <div id="paywallModal" class="paywall-modal" style="display: none;">
        <div class="paywall-modal-backdrop"></div>
        <div class="paywall-modal-content">
          <button class="paywall-modal-close">&times;</button>
          <div class="paywall-icon">&#x1F512;</div>
          <h2 class="paywall-title"></h2>
          <p class="paywall-message"></p>
          <button class="btn btn-primary paywall-cta"></button>
          <div class="paywall-credits" style="display: none;">
            <div class="paywall-credits-title">Or use credits</div>
            <div class="paywall-credits-info"></div>
            <button class="btn paywall-use-credits">Use Credits</button>
          </div>
          <div class="paywall-secondary">
            <a href="#" class="paywall-dismiss">Maybe later</a>
          </div>
        </div>
      </div>
    `;
    document.body.insertAdjacentHTML('beforeend', modalHTML);
    modal = document.getElementById('paywallModal');

    // Close handlers
    modal.querySelector('.paywall-modal-backdrop').addEventListener('click', () => hidePaywallModal());
    modal.querySelector('.paywall-modal-close').addEventListener('click', () => hidePaywallModal());
    modal.querySelector('.paywall-dismiss').addEventListener('click', (e) => {
      e.preventDefault();
      hidePaywallModal();
    });

    // ESC key handler
    modal.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        hidePaywallModal();
      }
      // Focus trap: keep Tab within modal
      if (e.key === 'Tab') {
        const focusable = modal.querySelectorAll('button, a[href], [tabindex]:not([tabindex="-1"])');
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    });
  }

  // Update modal content
  modal.querySelector('.paywall-title').textContent = options.title || 'Upgrade Required';
  modal.querySelector('.paywall-message').textContent = options.message || '';
  modal.querySelector('.paywall-cta').textContent = options.ctaText || 'Upgrade Now';

  // Handle CTA click
  const ctaBtn = modal.querySelector('.paywall-cta');
  ctaBtn.onclick = () => {
    hidePaywallModal();
    if (options.ctaText === 'Sign Up Free') {
      authUI.showModal('signup');
    } else {
      // Show pricing modal
      pricingModal.show('paywall');
    }
  };

  // Handle credit option
  const creditsSection = modal.querySelector('.paywall-credits');
  if (options.showCreditOption && options.creditsAvailable >= options.creditsRequired) {
    creditsSection.style.display = 'block';
    modal.querySelector('.paywall-credits-info').textContent =
      `Use ${options.creditsRequired} credits (you have ${options.creditsAvailable})`;
    modal.querySelector('.paywall-use-credits').onclick = () => {
      hidePaywallModal();
      if (options.onUseCredits) options.onUseCredits();
    };
  } else {
    creditsSection.style.display = 'none';
  }

  // Show modal and focus for keyboard accessibility
  modal.style.display = 'flex';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  const closeBtn = modal.querySelector('.paywall-modal-close');
  if (closeBtn) closeBtn.focus();
}

/**
 * Hide paywall modal
 */
function hidePaywallModal() {
  const modal = document.getElementById('paywallModal');
  if (modal) {
    modal.style.display = 'none';
  }
}

/**
 * Main application initialization
 */
export async function initApp() {
  if (typeof __BUILD_BRANCH__ !== 'undefined') {
    console.log(`[ShowLark] branch=${__BUILD_BRANCH__} commit=${__BUILD_COMMIT__} built=${__BUILD_TIME__}`);
  }

  // Initialize analytics FIRST (before auth) to capture PostHog ID
  // Wrapped in try/catch: analytics must never block app startup
  // (e.g., PostHog/GA4 CDN blocked by ad blocker)
  try {
    await initAnalytics();
  } catch (e) {
    console.warn('[App] Analytics init failed (non-critical):', e.message);
  }

  // Initialize dev mode if in development
  await initDevMode();

  // Cache all DOM element references
  cacheElements();

  // Initialize Lucide icons
  if (window.lucide) {
    window.lucide.createIcons();
  }

  // Initialize managers
  managers.lyricsRenderer = new LyricsRenderer();
  managers.visualizerManager = new VisualizerManager(elements.canvas);
  managers.perfMonitor = perfMonitor;

  // Reset audio feature extractor when visualizer changes
  // This ensures fresh beat detection history for the new visualizer
  managers.visualizerManager.setOnVisualizerChange(() => {
    if (audioState.featureExtractor) {
      audioState.featureExtractor.reset();
    }
  });

  // Initialize new UI systems
  initOverlayPanels();
  initDropModal();
  initSettingsOverlay();
  initBackgroundController();
  initPlaylistOverlay();
  initPlaylistGenerator();
  initLibraryBrowser();
  initSunoManager();
  initSunoOnboarding();

  // Set up all event handlers
  setupFileHandlers();
  setupPlaylistButtonListeners();
  setupPlaylistSync();
  setupPlaylistHorizontalScroll();
  setupMobileCollapsiblePanels();
  setupPlaybackControls();
  setupVizTypeChangeHandler();
  setupLyricsPanelListeners();
  setupLyricsSettingsListeners();
  setupPerformanceMonitorListeners();
  setupPresetListeners();
  setupDisplayModeListeners();
  setupLiveModeListeners();
  setupResolutionChangeHandler();
  setupFpsChangeHandler();
  setupCodecBitrateHandlers();
  setupExportButton();
  setupExportPlaylistSummary();
  setupExportStatusBar();

  // Async initialization - populate legacy dropdown (grid needs auth first)
  await populateVizSelector();

  // Initialize auth and entitlements (must happen before building grid for user visualizers)
  await initAuth();

  // Complete analytics setup AFTER auth (links PostHog ID to user, tracks session)
  try {
    await completeAnalyticsSetup();
  } catch (e) {
    console.warn('[App] Analytics setup failed (non-critical):', e.message);
  }

  // Build visualizer grid after auth is initialized (so user visualizers can be fetched)
  await buildVisualizerGrid();

  // Update export options with tier indicators
  await updateExportOptionsWithTiers();

  // Initialize presets dropdown (async, after auth + preset service init)
  await populatePresetSelect();

  // Note: Canvas starts blank intentionally (no visualizer initialized)
  // Visualization activates lazily on first playback, live mode entry, or export
  // This prevents rendering the visualizer background before user interaction

  console.log('[App] Initialization complete');
}
